import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { movies, categories } from '@/mocks/movies';
import MovieCard from '@/components/feature/MovieCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const [activeTag, setActiveTag] = useState<string>('全部');
  const [sortBy, setSortBy] = useState<string>('hot');

  const filteredMovies = useMemo(() => {
    let result = movies.filter((m) => m.categorySlug === slug);
    if (activeTag !== '全部') {
      result = result.filter((m) => m.tags.includes(activeTag));
    }
    if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'new') {
      result = [...result].sort((a, b) => b.year - a.year);
    }
    return result;
  }, [slug, activeTag, sortBy]);

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    movies.filter((m) => m.categorySlug === slug).forEach((m) => {
      m.tags.forEach((t) => tagSet.add(t));
    });
    return ['全部', ...Array.from(tagSet)];
  }, [slug]);

  if (!currentCategory) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-foreground-500">分类不存在</p>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: currentCategory.name },
  ];

  const pageTitle = `${currentCategory.name} - 麻花影院免费观看 | 免费${currentCategory.name}在线播放`;
  const pageDesc = `麻花影院提供海量${currentCategory.name}免费在线观看，涵盖最新热门${currentCategory.name}、经典${currentCategory.name}全集，高清画质不卡顿，更新快，无需付费即可畅享${currentCategory.name}在线播放体验。`;

  return (
    <>
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: pageTitle,
        description: pageDesc,
        url: `https://mahua.tv/category/${slug}`,
        isPartOf: { '@type': 'WebSite', name: '麻花影院', url: 'https://mahua.tv' },
      })}</script>
      <main className="min-h-screen pt-20 pb-12">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <div className="mb-8">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                {currentCategory.name}
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                共 {filteredMovies.length} 部{currentCategory.name}资源
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div className="flex flex-wrap items-center gap-2">
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => setActiveTag(tag)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                      activeTag === tag
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2 bg-background-100 rounded-full px-1 py-1">
                {[
                  { key: 'hot', label: '热门' },
                  { key: 'new', label: '最新' },
                  { key: 'rating', label: '评分' },
                ].map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setSortBy(s.key)}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                      sortBy === s.key
                        ? 'bg-background-50 text-foreground-950 shadow-sm'
                        : 'text-foreground-500 hover:text-foreground-800'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {filteredMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {filteredMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
                  <i className="ri-movie-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">暂无相关影视资源</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
}